import { NormalizeScanCode } from '../collector/collectorScan';

/**
 * NÚCLEO NÃO-VISUAL DA FÁBRICA (M6) — leitura dos envelopes do backend e as
 * poucas transformações de DADO que as telas compartilham.
 *
 * ⚠️ O que NÃO mora aqui, de propósito: regra de negócio. Quem decide se o LT
 * vira, se a combinação do kit fecha e se o refugo estourou o teórico é o
 * backend (`productionLotEngine.js` / `productionRules.js`), e a resposta já vem
 * com o veredito pronto (`lot_decision`, `scrap`, os 400/409 com mensagem). O
 * front LÊ esse veredito e escolhe a tela. Recalcular qualquer uma dessas contas
 * aqui criaria uma segunda verdade que só diverge quando ninguém está olhando —
 * e num galpão a divergência aparece como "o sistema disse uma coisa na tela e
 * outra no relatório".
 */

// ── Ordem de produção ───────────────────────────────────────────────────────

export const PRODUCTION_ORDER_STATUSES = Object.freeze(['PLANNED', 'RUNNING', 'DONE', 'CANCELED']);

/**
 * Os estados em que a bancada age na OP (espelha o backend): SÓ liberada.
 * Planejada aparece na lista, mas espera o supervisor liberar.
 */
export const PRODUCTION_ORDER_OPERABLE_STATUSES = Object.freeze(['RUNNING']);

export function IsProductionOrderOperable(order) {
    return PRODUCTION_ORDER_OPERABLE_STATUSES.includes(String(order?.status || ''));
}

/**
 * O que a OP é PARA ESTA BANCADA (a OP não tem máquina: a bancada a abre).
 * `canOpen` só para OP liberada que está livre ou já aberta nesta máquina;
 * rodando em outra máquina ou ainda planejada aparece, mas não abre.
 * @returns {{ key: 'here'|'free'|'elsewhere'|'planned'|'closed', canOpen: boolean, variant: string }}
 */
export function BenchOrderState(order, machine) {
    const status = String(order?.status || '');
    if (status === 'PLANNED') {
        return { key: 'planned', canOpen: false, variant: 'info' };
    }
    if (status !== 'RUNNING') {
        return { key: 'closed', canOpen: false, variant: 'neutral' };
    }
    if (!order?.machine_id) {
        return { key: 'free', canOpen: true, variant: 'success' };
    }
    if (machine?.id && order.machine_id === machine.id) {
        return { key: 'here', canOpen: true, variant: 'success' };
    }
    return { key: 'elsewhere', canOpen: false, variant: 'warning' };
}

const BENCH_ORDER_RANK = { here: 0, free: 1, elsewhere: 2, planned: 3 };

/** Lista da bancada: sem repetidas e sem encerradas; a aberta aqui primeiro, depois livres, em outra máquina e planejadas. */
export function SortBenchOrders(rows, machine) {
    const seen = new Set();
    return (Array.isArray(rows) ? rows : [])
        .filter((row) => {
            if (!row?.id || seen.has(row.id)) {
                return false;
            }
            seen.add(row.id);
            return BenchOrderState(row, machine).key !== 'closed';
        })
        .sort(
            (a, b) =>
                BENCH_ORDER_RANK[BenchOrderState(a, machine).key] - BENCH_ORDER_RANK[BenchOrderState(b, machine).key] ||
                String(a.code).localeCompare(String(b.code)),
        );
}

/**
 * Ainda está aberta NESTA máquina? É o que a bancada confere a cada volta: o
 * gestor pode finalizar/cancelar, o supervisor pode tirar da bancada.
 */
export function IsOpenAtMachine(order, machine) {
    return String(order?.status || '') === 'RUNNING' && Boolean(machine?.id) && order?.machine_id === machine.id;
}

/**
 * A mensagem da tela padrão quando a OP SAIU desta bancada sem o operador
 * pedir (finalizada ou cancelada pelo painel, tirada da bancada, aberta em
 * outra máquina). Lê o estado real da OP e o último evento do histórico.
 * @returns {{ tone: 'warning', title: string, text: string }}
 */
export function DescribeBenchExit(order, label, format) {
    const code = order?.code ?? '';
    const events = Array.isArray(order?.events) ? order.events : [];
    const last = events[0] ?? null;
    const status = String(order?.status || '');
    let key = 'free';
    if (status === 'DONE') {
        key = last?.event_type === 'FINISHED' && last?.reason === 'console' ? 'finished_console' : 'finished';
    } else if (status === 'CANCELED') {
        key = 'canceled';
    } else if (status === 'PLANNED') {
        key = 'planned';
    } else if (order?.machine_id) {
        key = 'elsewhere';
    } else if (last?.event_type === 'REMOVED_FROM_BENCH') {
        key = 'removed';
    }
    return {
        tone: 'warning',
        title: format(label('wms.bench.notice_left_title'), { code }),
        text: format(label(`wms.bench.notice_left_${key}`), { machine: order?.machine_code ?? '—' }),
    };
}

/**
 * O que aconteceu com cada pallet de saída que encerrou (a OP terminou, ou o
 * operador bipou outro pallet — `putaway`/`ended` na resposta): ganhou a OS
 * de transporte para a logística, voltou ao rolo vazio, já tinha saído da
 * máquina (guardado por outro movimento), ou a OS não saiu (e por quê). Uma
 * frase por pallet.
 */
export function DescribePutaway(putaway, label, format) {
    return (Array.isArray(putaway) ? putaway : []).map((row) =>
        format(label(`wms.production.putaway_${row.action}`), {
            lpn: row.lpn_code ?? '—',
            os: row.service_order_code ?? '—',
            local: row.from_location_code ?? '—',
        }),
    );
}

/** Algum pallet foi para a logística (OS de transporte)? A bancada diz para aguardar a coleta. */
export function PutawayWaitsLogistics(putaway) {
    return (Array.isArray(putaway) ? putaway : []).some((row) =>
        ['service_order', 'already_scheduled'].includes(row.action),
    );
}

// O código de LPN (§4): PREFIXO-AAMMDD-SEQ — o mesmo padrão do backend
// (lpnCodes.js). A bancada separa por ele o bipe do PALLET do bipe do KIT
// (etiqueta do Full, sem hífen).
const LPN_CODE_PATTERN = /^[A-Z][A-Z0-9]{0,7}-\d{6}-\d{3}$/;

export function IsLpnCode(value) {
    return LPN_CODE_PATTERN.test(
        String(value ?? '')
            .trim()
            .toUpperCase(),
    );
}

/**
 * O bipe do pallet na bancada: o pallet anterior que encerrou (se houve) e
 * onde o bipado ficou — na saída da máquina, ou em formação (a saída ainda tem
 * o anterior; o operador o bipa de novo quando a logística o levar).
 */
export function DescribeLpnScan(result, label, format) {
    const lpn = result?.lpn?.code ?? '—';
    const location = result?.output_location_code ?? '—';
    const key = result?.output_state === 'at_output' ? 'wms.bench.lpn_at_output' : 'wms.bench.lpn_forming';
    return [...DescribePutaway(result?.ended ? [result.ended] : [], label, format), format(label(key), { lpn, location })];
}

/**
 * Os contadores da bancada: kits apontados na OP e, com meta, quantos faltam e
 * o progresso em %. Nunca passa de 100% nem fica negativo.
 */
export function BenchProgress(kitsDone, plannedKits) {
    const done = Number(kitsDone) || 0;
    const planned = Number(plannedKits) || 0;
    if (planned <= 0) {
        return { done, planned: null, remaining: null, percent: null };
    }
    return {
        done,
        planned,
        remaining: Math.max(planned - done, 0),
        percent: Math.min(Math.floor((done / planned) * 100), 100),
    };
}

export function ProductionOrderStatusVariant(status) {
    switch (status) {
        case 'RUNNING':
            return 'success';
        case 'PLANNED':
            return 'info';
        case 'CANCELED':
            return 'danger';
        default:
            return 'neutral';
    }
}

/**
 * As posições da OP separadas por PONTA. A regra (v) das cinco de LPN é
 * literal: logística ENTREGA em IN e COLETA em OT — a tela nunca oferece uma
 * pela outra.
 */
export function SplitProductionLocations(order) {
    const rows = Array.isArray(order?.locations) ? order.locations : [];
    return {
        all: rows,
        inputs: rows.filter((row) => row.location_type === 'FACTORY_IN'),
        outputs: rows.filter((row) => row.location_type === 'FACTORY_OUT'),
    };
}

/**
 * O LT VIGENTE da OP. `production_lots` da ficha vem ordenado por `created_at`
 * DESC (é a mesma consulta que o serviço usa em `findCurrentProductionLot`),
 * então o vigente é o primeiro. Ler o primeiro em vez de reordenar mantém uma
 * fonte só para "qual é o lote de agora".
 */
export function ResolveCurrentProductionLot(order) {
    const lots = Array.isArray(order?.production_lots) ? order.production_lots : [];
    return lots[0] ?? null;
}

// ── Lote do fornecedor: casamento LOCAL do bipe com o item do pallet ─────────

/**
 * Forma comparável de um lote de fornecedor. Reusa a normalização do bipe do
 * coletor (caixa alta, sem espaço) para os DOIS lados da comparação — é uma
 * comparação de tela, tolerante, e ela não vai para o wire.
 */
export function NormalizeSupplierLot(value) {
    return NormalizeScanCode(value);
}

/**
 * "Qual pallet da ENTRADA tem este lote?"
 *
 * O PRD é explícito: na boca da máquina o operador bipa o LOTE DO FORNECEDOR
 * (a etiqueta colada na bobina), não o LPN. Só que o wire de consumo precisa de
 * `lpn_code` + `product_id`. Este casamento é a ponte: procura o item nos
 * pallets que estão nas posições de entrada DA OP e devolve os candidatos.
 *
 * ⚠️ O `supplier_lot` devolvido é o do ITEM (valor gravado no banco), nunca a
 * string bipada: o backend casa o item por igualdade do lote, e mandar de volta
 * exatamente o que ele guardou elimina a chance de um espaço ou um acento de
 * digitação virar "lote não está no LPN".
 *
 * @param {Array} lpns  pallets com `items` (detalhe de `/warehouse/lpns/:code`)
 * @param {string} scannedLot o que foi bipado
 * @returns {Array<{lpn, item, supplier_lot, available_qty}>}
 */
export function FindSupplierLotCandidates(lpns, scannedLot) {
    const target = NormalizeSupplierLot(scannedLot);
    if (!target) {
        return [];
    }
    const candidates = [];
    for (const lpn of Array.isArray(lpns) ? lpns : []) {
        for (const item of Array.isArray(lpn?.items) ? lpn.items : []) {
            if (NormalizeSupplierLot(item?.supplier_lot) !== target) {
                continue;
            }
            const availableQty = Number(item?.qty ?? 0);
            if (!(availableQty > 0)) {
                continue;
            }
            candidates.push({ lpn, item, supplier_lot: item.supplier_lot, available_qty: availableQty });
        }
    }
    return candidates;
}

/** Todos os lotes distintos disponíveis nos pallets da entrada (a lista de "o que dá para bipar"). */
export function CollectAvailableSupplierLots(lpns) {
    const seen = new Map();
    for (const lpn of Array.isArray(lpns) ? lpns : []) {
        for (const item of Array.isArray(lpn?.items) ? lpn.items : []) {
            const lot = item?.supplier_lot;
            if (!lot || !(Number(item?.qty ?? 0) > 0)) {
                continue;
            }
            const key = NormalizeSupplierLot(lot);
            const previous = seen.get(key);
            seen.set(key, {
                supplier_lot: lot,
                qty: (previous?.qty ?? 0) + Number(item.qty),
                lpn_codes: [...new Set([...(previous?.lpn_codes ?? []), lpn.code])],
                sku: item.sku ?? previous?.sku ?? null,
                name: item.name ?? previous?.name ?? null,
            });
        }
    }
    return [...seen.values()];
}

// ── O veredito do motor de LT, lido (não recalculado) ───────────────────────

/** Os três desfechos que a bancada precisa MOSTRAR de forma diferente. */
export const LOT_VERDICT_OPENED = 'OPENED';
export const LOT_VERDICT_HELD = 'HELD';
export const LOT_VERDICT_KEPT = 'KEPT';

/**
 * Traduz o `lot_decision` da resposta de consumo no desfecho que a tela mostra.
 *
 * É LEITURA: `action`, `deferred` e `transition_kit` já vêm decididos pelo motor
 * do backend. O que se decide aqui é só qual das três telas aparece —
 * "virou o lote", "a virada está segura pelo kit em aberto" ou "mesmo lote".
 */
export function ReadLotDecision(decision) {
    if (!decision || typeof decision !== 'object') {
        return null;
    }
    const verdict =
        decision.action === 'OPEN'
            ? LOT_VERDICT_OPENED
            : decision.deferred || decision.transition_kit
              ? LOT_VERDICT_HELD
              : LOT_VERDICT_KEPT;
    return {
        verdict,
        reasons: Array.isArray(decision.reasons) ? decision.reasons : [],
        transition_kit: Boolean(decision.transition_kit),
        kit_open: Boolean(decision.kit_open),
        previous_lot_code: decision.previous_lot_code ?? null,
        opening_supplier_lot: decision.opening_supplier_lot ?? null,
    };
}

/**
 * O texto de "por que virou": junta os motivos do motor em uma frase. Os
 * motivos são chaves do backend (`SUPPLIER_LOT_CHANGE`…) e viram rótulo aqui.
 */
export function FormatLotReasons(reasons, label) {
    const parts = (Array.isArray(reasons) ? reasons : []).map((reason) => label(`wms.bench.reason_${reason}`));
    if (parts.length === 0) {
        return '';
    }
    if (parts.length === 1) {
        return parts[0];
    }
    return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

// ── Genealogia ──────────────────────────────────────────────────────────────

/**
 * Achata a árvore de `sources` da genealogia em linhas com PROFUNDIDADE, para a
 * tela desenhar a hierarquia sem recursão de componente (e sem risco de
 * estourar a pilha num recall com muitos níveis — o backend já limita a 10).
 *
 * Cada linha: `{depth, supplier_lot, kind, origins, lot}` onde `lot` é o LT
 * filho quando a fonte é um lote fabricado aqui e `origins` são os pallets que
 * trouxeram aquele lote — com a ordem de compra e o fornecedor quando a entrada
 * veio de compra. É `origins` que faz a árvore chegar até a ponta.
 */
export function FlattenGenealogySources(node, depth = 0, accumulator = []) {
    for (const source of Array.isArray(node?.sources) ? node.sources : []) {
        accumulator.push({
            depth,
            supplier_lot: source.supplier_lot,
            kind: source.kind,
            origins: Array.isArray(source.origins) ? source.origins : [],
            lot: source.production_lot ?? null,
        });
        if (source.production_lot) {
            FlattenGenealogySources(source.production_lot, depth + 1, accumulator);
        }
    }
    return accumulator;
}

/** Os LPNs distintos que ALIMENTARAM o lote. */
export function CollectConsumptionLpnCodes(genealogy) {
    const codes = new Set();
    for (const consumption of Array.isArray(genealogy?.consumptions) ? genealogy.consumptions : []) {
        if (consumption?.lpn_code) {
            codes.add(consumption.lpn_code);
        }
    }
    return [...codes];
}

// ── A origem da entrada (o elo até o FORNECEDOR) ────────────────────────────

/**
 * O veredito que o backend manda em `lpn_origin.resolution` — só o que a tela
 * precisa DISTINGUIR visualmente. A frase de cada um vem do dicionário
 * (`wms.lpn_origin_resolution.*`), não daqui: repetir texto no código é a
 * receita para a tela e o dicionário divergirem.
 */
export const ORIGIN_RESOLUTION_PURCHASE_ORDER = 'PURCHASE_ORDER';

/** A origem fechou na compra? (é o único caso que tem OC e fornecedor para mostrar) */
export function IsPurchaseOrderOrigin(origin) {
    return origin?.resolution === ORIGIN_RESOLUTION_PURCHASE_ORDER;
}

/**
 * O tom da origem na tela. Só a compra é "resolvida"; o resto é informação
 * honesta de que a cadeia parou antes — e `PURCHASE_ORDER_NOT_FOUND` é o único
 * que merece alerta, porque é o dado dizendo que aponta para uma compra que não
 * existe mais (vínculo órfão), e não uma origem legítima de outro tipo.
 */
export function OriginVariant(origin) {
    switch (origin?.resolution) {
        case 'PURCHASE_ORDER':
            return 'success';
        case 'PURCHASE_ORDER_NOT_FOUND':
        case 'LPN_NOT_FOUND':
            return 'warning';
        default:
            return 'neutral';
    }
}

/**
 * O texto de UMA origem, montado das partes que existem.
 *
 * ⚠️ Nunca devolve string vazia: origem sem compra é uma FRASE dizendo o que é
 * (o pedido do dono foi literal — "nunca em branco, e nunca fingindo que é
 * compra"). Quando é `OTHER_ORIGIN`, o tipo do LPN entra junto, porque
 * "não é compra" sozinho não diz se veio de produção, devolução ou repack.
 */
export function DescribeOrigin(origin, label) {
    const resolution = origin?.resolution ?? 'NO_ORIGIN';
    const base = label(`wms.lpn_origin_resolution.${resolution}`);
    if (resolution !== 'OTHER_ORIGIN' || !origin?.origin_type) {
        return base;
    }
    return `${base}: ${label(`wms.lpn_origin.${origin.origin_type}`)}`;
}

// ── Números da bancada ──────────────────────────────────────────────────────

/**
 * Quantidade legível sem casas decimais inúteis: o wire é DECIMAL(18,6) e
 * `12.000000` na tela de um operador é ruído. Não arredonda valor real — só
 * poda zeros à direita.
 */
export function FormatQty(value) {
    const number = Number(value ?? 0);
    if (!Number.isFinite(number)) {
        return '0';
    }
    return String(Number(number.toFixed(6)));
}

/** Percentual com 1 casa — usado só para EXIBIR o que o backend calculou. */
export function FormatPct(value) {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? `${number.toFixed(1).replace('.', ',')}%` : '—';
}
